import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, ReactNode, KeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';

import {
  api,
  Bucket,
  BucketCreatePayload,
  BucketUpdatePayload,
  Project,
  ProjectCreatePayload,
  ProjectUpdatePayload,
  RelatedEntitySummary,
  Task,
  TaskCreatePayload,
  TaskFilter,
  TaskUpdatePayload,
  TaskStatus,
} from '../../../api';
import type { FeatureContext } from '../../types';
import { formatDate } from '../../shared/formatters';
import { MemoryRouter, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  BUCKET_FIELDS,
  BUCKET_INITIAL_VALUES,
  BucketFormValues,
  ConfigurableField,
  EntityField,
  PROJECT_FIELDS,
  PROJECT_INITIAL_VALUES,
  ProjectFormValues,
  RelationField,
  SelectOption,
  TASK_FIELDS,
  TASK_INITIAL_VALUES,
  TaskFormValues,
} from './task-config';
import taskFilterConfig from './task-filters.json';
import './task-management.css';

type TaskFilterOption = TaskFilter | 'all';

interface TaskFilterDefinition {
  label: string;
  value: TaskFilterOption;
}

type EntityKey<T> = Extract<keyof T, string>;

type RelationOption = {
  id: number;
  name: string;
};

const TASK_FILTER_DEFINITIONS: TaskFilterDefinition[] = taskFilterConfig as TaskFilterDefinition[];

const PROJECT_STATUS_ORDER: TaskStatus[] = ['active', 'ongoing', 'backlog', 'completed', 'archived'];
const PROJECT_STATUS_TITLES: Record<TaskStatus, string> = {
  active: 'Active Projects',
  ongoing: 'Ongoing Projects',
  backlog: 'Backlog',
  completed: 'Completed',
  archived: 'Archived',
};

function isoDateToInput(value: string | null): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (input: number) => input.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function dateInputToIso(value: string): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizeName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function toggleSelection(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id];
}

function taskMatchesFilter(task: Task, filter: TaskFilterOption, referenceDate = new Date()): boolean {
  if (filter === 'all') {
    return true;
  }

  if (!task.timeStarted) {
    return false;
  }

  const taskDate = new Date(task.timeStarted);
  if (Number.isNaN(taskDate.getTime())) {
    return false;
  }

  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = startOfDay(referenceDate);

  if (filter === 'today') {
    return taskDate >= today && taskDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
  }

  if (filter === 'tomorrow') {
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    return taskDate >= tomorrow && taskDate < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000);
  }

  if (filter === 'this_week') {
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diff);
    const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    return taskDate >= weekStart && taskDate < weekEnd;
  }

  if (filter === 'overdue') {
    return task.status !== 'completed' && taskDate < today;
  }

  return true;
}

function useTaskManagementData() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [taskData, projectData, bucketData] = await Promise.all([
        api.listTasks(),
        api.listProjects(),
        api.listBuckets(),
      ]);
      setTasks(taskData);
      setProjects(projectData);
      setBuckets(bucketData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load task management data.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  return {
    tasks,
    projects,
    buckets,
    setTasks,
    setProjects,
    setBuckets,
    loading,
    error,
    reload: loadAll,
  };
}

function TaskFilterToggle({
  label,
  value,
  active,
  onSelect,
}: {
  label: string;
  value: TaskFilterOption;
  active: boolean;
  onSelect: (value: TaskFilterOption) => void;
}) {
  return (
    <button
      type="button"
      className={active ? 'task-filter task-filter--active' : 'task-filter'}
      onClick={() => onSelect(value)}
    >
      {label}
    </button>
  );
}

function TaskFilters({
  activeFilter,
  onChange,
  definitions = TASK_FILTER_DEFINITIONS,
}: {
  activeFilter: TaskFilterOption;
  onChange: (filter: TaskFilterOption) => void;
  definitions?: TaskFilterDefinition[];
}) {
  return (
    <div className="task-filter-bar" role="group" aria-label="Task filters">
      {definitions.map((filter) => (
        <TaskFilterToggle
          key={filter.value}
          label={filter.label}
          value={filter.value}
          active={filter.value === activeFilter}
          onSelect={onChange}
        />
      ))}
    </div>
  );
}

function TaskCard({ task, onOpen }: { task: Task; onOpen: (task: Task) => void }) {
  return (
    <li className="task-card">
      <button type="button" className="task-card__button" onClick={() => onOpen(task)}>
        <div className="task-card__header">
          <div className="task-card__title">
            <span className="task-card__name">{task.name}</span>
            {task.done && <span className="task-card__badge task-card__badge--done">Done</span>}
            {task.attemptWithAi && (
              <span className="task-card__badge task-card__badge--ai">AI ready</span>
            )}
          </div>
          <span className={`task-card__status task-card__status--${task.status}`}>
            {task.status.replace(/_/g, ' ')}
          </span>
        </div>
        <dl className="task-card__meta">
          <div>
            <dt>Urgency</dt>
            <dd>{task.urgency.replace(/_/g, ' ')}</dd>
          </div>
          <div>
            <dt>Importance</dt>
            <dd>{task.importance.replace(/_/g, ' ')}</dd>
          </div>
          <div>
            <dt>Difficulty</dt>
            <dd>{task.difficulty.replace(/_/g, ' ')}</dd>
          </div>
          <div>
            <dt>Estimate</dt>
            <dd>
              {task.timeEstimateMinutes != null ? `${task.timeEstimateMinutes} min` : '—'}
            </dd>
          </div>
        </dl>
        {(task.projects.length > 0 || task.buckets.length > 0) && (
          <div className="task-card__associations">
            {task.projects.length > 0 && (
              <span>
                Projects: {task.projects.map((project) => project.name).join(', ')}
              </span>
            )}
            {task.buckets.length > 0 && (
              <span>
                Buckets: {task.buckets.map((bucket) => bucket.name).join(', ')}
              </span>
            )}
          </div>
        )}
      </button>
    </li>
  );
}

interface BaseModalProps {
  title: string;
  onClose: () => void;
  width?: 'narrow' | 'wide';
  children: ReactNode;
}

function Modal({ title, onClose, width = 'wide', children }: BaseModalProps) {
  return (
    <div className="task-modal" role="dialog" aria-modal="true" aria-labelledby="task-modal-title">
      <div className={width === 'narrow' ? 'task-modal__content task-modal__content--narrow' : 'task-modal__content'}>
        <header className="task-modal__header">
          <h3 id="task-modal-title">{title}</h3>
          <button type="button" className="task-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="task-modal__body">{children}</div>
      </div>
    </div>
  );
}

function RelationMultiSelectField({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
  onCreate,
  createLabel,
}: {
  label: string;
  options: RelationOption[];
  selected: number[];
  onChange: (value: number[]) => void;
  emptyLabel?: string;
  onCreate?: () => void;
  createLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current) {
        return;
      }
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const selectedItems = useMemo(
    () => options.filter((option) => selected.includes(option.id)),
    [options, selected],
  );

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return options;
    }
    return options.filter((option) => option.name.toLowerCase().includes(term));
  }, [options, query]);

  const handleToggle = useCallback(
    (id: number) => {
      onChange(toggleSelection(selected, id));
    },
    [onChange, selected],
  );

  const handleControlClick = useCallback(() => {
    setOpen((current) => !current);
  }, []);

  const handleControlKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        if (searchRef.current) {
          searchRef.current.focus();
        }
      }
    },
    [],
  );

  const handleOptionClick = useCallback(
    (event: ReactMouseEvent<HTMLLabelElement, MouseEvent>, id: number) => {
      event.preventDefault();
      handleToggle(id);
    },
    [handleToggle],
  );

  const handleClearSearch = useCallback(() => {
    setQuery('');
  }, []);

  const handleSearchKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  }, []);

  return (
    <div className="form-field multi-select" ref={containerRef}>
      <span className="multi-select__label">{label}</span>
      <button
        type="button"
        className={open ? 'multi-select__control multi-select__control--open' : 'multi-select__control'}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleControlClick}
        onKeyDown={handleControlKeyDown}
      >
        {selectedItems.length === 0 ? (
          <span className="multi-select__placeholder">Select {label.toLowerCase()}</span>
        ) : (
          <span className="multi-select__values">
            {selectedItems.map((item) => (
              <span key={item.id} className="multi-select__value">
                {item.name}
              </span>
            ))}
          </span>
        )}
        <span className="multi-select__chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="multi-select__popover">
          <div className="multi-select__search-row">
            <input
              ref={searchRef}
              type="search"
              className="multi-select__search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${label.toLowerCase()}`}
              onKeyDown={handleSearchKeyDown}
            />
            {query && (
              <button type="button" className="multi-select__clear" onClick={handleClearSearch} aria-label="Clear search">
                ×
              </button>
            )}
          </div>
          <ul className="multi-select__options" role="listbox" aria-multiselectable="true">
            {filteredOptions.length === 0 ? (
              <li className="multi-select__empty">{emptyLabel ?? 'No results found.'}</li>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected.includes(option.id);
                return (
                  <li key={option.id}>
                    <label
                      className={isSelected ? 'multi-select__option multi-select__option--selected' : 'multi-select__option'}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => handleOptionClick(event, option.id)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggle(option.id)}
                      />
                      <span>{option.name}</span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
          {onCreate && (
            <button type="button" className="link-button multi-select__create" onClick={onCreate}>
              {createLabel ? (createLabel.startsWith('+') ? createLabel : `+ ${createLabel}`) : `+ Create ${label.toLowerCase()}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function RelationSingleSelectField({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
  allowNone,
  onCreate,
  createLabel,
}: {
  label: string;
  options: RelationOption[];
  selected: number | null;
  onChange: (value: number | null) => void;
  emptyLabel?: string;
  allowNone?: boolean;
  onCreate?: () => void;
  createLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current) {
        return;
      }
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const selectedOption = useMemo(
    () => options.find((option) => option.id === selected) ?? null,
    [options, selected],
  );

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return options;
    }
    return options.filter((option) => option.name.toLowerCase().includes(term));
  }, [options, query]);

  const handleSelect = useCallback(
    (value: number | null) => {
      onChange(value);
      setOpen(false);
    },
    [onChange],
  );

  const handleControlClick = useCallback(() => {
    setOpen((current) => !current);
  }, []);

  const handleControlKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  const handleSearchKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  }, []);

  return (
    <div className="form-field multi-select" ref={containerRef}>
      <span className="multi-select__label">{label}</span>
      <button
        type="button"
        className={open ? 'multi-select__control multi-select__control--open' : 'multi-select__control'}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleControlClick}
        onKeyDown={handleControlKeyDown}
      >
        {selectedOption ? (
          <span className="multi-select__values">
            <span className="multi-select__value">{selectedOption.name}</span>
          </span>
        ) : (
          <span className="multi-select__placeholder">{allowNone ? `Select ${label.toLowerCase()}` : emptyLabel ?? 'No options available.'}</span>
        )}
        <span className="multi-select__chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="multi-select__popover">
          <div className="multi-select__search-row">
            <input
              ref={searchRef}
              type="search"
              className="multi-select__search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${label.toLowerCase()}`}
              onKeyDown={handleSearchKeyDown}
            />
            {query && (
              <button type="button" className="multi-select__clear" onClick={() => setQuery('')} aria-label="Clear search">
                ×
              </button>
            )}
          </div>
          <ul className="multi-select__options" role="listbox">
            {allowNone && (
              <li>
                <button
                  type="button"
                  className={selected == null ? 'multi-select__option multi-select__option--selected' : 'multi-select__option'}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(null)}
                >
                  <span>{emptyLabel ?? 'None selected'}</span>
                </button>
              </li>
            )}
            {filteredOptions.length === 0 ? (
              <li className="multi-select__empty">{emptyLabel ?? 'No results found.'}</li>
            ) : (
              filteredOptions.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    className={option.id === selected ? 'multi-select__option multi-select__option--selected' : 'multi-select__option'}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(option.id)}
                  >
                    <span>{option.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
          {onCreate && (
            <button type="button" className="link-button multi-select__create" onClick={onCreate}>
              {createLabel ?? `+ Create ${label.toLowerCase()}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function isRelationField<TForm>(field: EntityField<TForm>): field is RelationField<TForm> {
  return field.type === 'relation-multi' || field.type === 'relation-single';
}

function isConfigurableField<TForm>(field: EntityField<TForm>): field is ConfigurableField<TForm> {
  return !isRelationField(field);
}

function renderEntityField<TForm extends Record<string, unknown>>({
  field,
  values,
  onChange,
  relationOptions,
  relationCreators,
}: {
  field: EntityField<TForm>;
  values: TForm;
  onChange: <K extends keyof TForm>(key: K, value: TForm[K]) => void;
  relationOptions: Partial<Record<EntityKey<TForm>, RelationOption[]>>;
  relationCreators: Partial<Record<EntityKey<TForm>, () => void>>;
}) {
  if (!isConfigurableField(field)) {
    const key = field.key as EntityKey<TForm>;
    const createHandler = relationCreators[key];

    if (field.type === 'relation-multi') {
      const selected = (values[key] as number[]) ?? [];
      const allowCreate = createHandler && (field.createVisibility !== 'empty' || selected.length === 0);
      return (
        <RelationMultiSelectField
          key={String(field.key)}
          label={field.label}
          options={relationOptions[key] ?? []}
          selected={selected}
          onChange={(next) => onChange(field.key, next as TForm[typeof field.key])}
          emptyLabel={field.emptyLabel}
          onCreate={allowCreate ? createHandler : undefined}
          createLabel={field.createLabel}
        />
      );
    }

    const selected = (values[key] as number | null) ?? null;
    const allowCreate = createHandler && (field.createVisibility !== 'empty' || selected == null);
    return (
      <RelationSingleSelectField
        key={String(field.key)}
        label={field.label}
        options={relationOptions[key] ?? []}
        selected={selected}
        allowNone={field.allowNone}
        emptyLabel={field.emptyLabel}
        onChange={(next) => onChange(field.key, next as TForm[typeof field.key])}
        onCreate={allowCreate ? createHandler : undefined}
        createLabel={field.createLabel}
      />
    );
  }

  const configurableField = field;
  const value = values[configurableField.key];

  if (configurableField.type === 'checkbox') {
    return (
      <label key={String(configurableField.key)} className="form-field form-field--checkbox">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(event) =>
            onChange(configurableField.key, event.target.checked as TForm[typeof configurableField.key])
          }
        />
        <span>{configurableField.label}</span>
      </label>
    );
  }

  if (configurableField.type === 'select') {
    const options = configurableField.options ?? [];
    return (
      <label key={String(configurableField.key)} className="form-field">
        <span>{configurableField.label}</span>
        <select
          value={(value as string) ?? ''}
          onChange={(event) =>
            onChange(configurableField.key, event.target.value as TForm[typeof configurableField.key])
          }
        >
          {options.map((option: SelectOption<string>) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const inputType =
    configurableField.type === 'datetime'
      ? 'datetime-local'
      : configurableField.type === 'date'
      ? 'date'
      : configurableField.type === 'number'
      ? 'number'
      : 'text';

  return (
    <label key={String(configurableField.key)} className="form-field">
      <span>{configurableField.label}</span>
      {configurableField.type === 'textarea' ? (
        <textarea
          value={(value as string) ?? ''}
          required={configurableField.required}
          placeholder={configurableField.placeholder}
          onChange={(event) =>
            onChange(configurableField.key, event.target.value as TForm[typeof configurableField.key])
          }
        />
      ) : (
        <input
          type={inputType}
          value={(value as string) ?? ''}
          required={configurableField.required}
          placeholder={configurableField.placeholder}
          onChange={(event) =>
            onChange(configurableField.key, event.target.value as TForm[typeof configurableField.key])
          }
        />
      )}
      {configurableField.description && <span className="form-hint">{configurableField.description}</span>}
    </label>
  );
}

function TaskFormDialog({
  title,
  mode,
  initialValues,
  projects,
  buckets,
  tasks,
  currentTask,
  onSubmit,
  onDelete,
  onClose,
  onCreateProject,
  onCreateBucket,
  onOpenProjectDetails,
  onOpenBucketDetails,
}: {
  title: string;
  mode: 'create' | 'edit';
  initialValues: TaskFormValues;
  projects: Project[];
  buckets: Bucket[];
  tasks: Task[];
  currentTask: Task | null;
  onSubmit: (payload: TaskCreatePayload | TaskUpdatePayload) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
  onCreateProject: () => void;
  onCreateBucket: () => void;
  onOpenProjectDetails?: (projectId: number) => void;
  onOpenBucketDetails?: (bucketId: number) => void;
}) {
  const [values, setValues] = useState<TaskFormValues>(initialValues);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = useCallback(<K extends keyof TaskFormValues>(key: K, value: TaskFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  }, []);

  const relationOptions = useMemo(
    () => ({
      parentTaskId: tasks
        .filter((task) => !currentTask || task.id !== currentTask.id)
        .map((task) => ({ id: task.id, name: task.name })),
      projectIds: projects.map((project) => ({ id: project.id, name: project.name })),
      bucketIds: buckets.map((bucket) => ({ id: bucket.id, name: bucket.name })),
    }),
    [tasks, currentTask, projects, buckets],
  );

  const relationCreators = useMemo(
    () => ({
      projectIds: onCreateProject,
      bucketIds: onCreateBucket,
    }),
    [onCreateProject, onCreateBucket],
  );

  const selectedParentTask = useMemo(
    () => (values.parentTaskId != null ? tasks.find((task) => task.id === values.parentTaskId) ?? null : null),
    [tasks, values.parentTaskId],
  );

  const selectedProjects = useMemo(
    () => projects.filter((project) => values.projectIds.includes(project.id)),
    [projects, values.projectIds],
  );

  const selectedBuckets = useMemo(
    () => buckets.filter((bucket) => values.bucketIds.includes(bucket.id)),
    [buckets, values.bucketIds],
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = normalizeName(values.name);
    if (!trimmed) {
      setError('Task name cannot be empty.');
      return;
    }

    const estimateText = values.timeEstimateMinutes.trim();
    let timeEstimateMinutes: number | null | undefined;

    if (estimateText !== '') {
      const parsed = Number(estimateText);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError('Time estimate must be a positive number.');
        return;
      }
      timeEstimateMinutes = Math.round(parsed);
    } else {
      timeEstimateMinutes = null;
    }

    const payload: TaskUpdatePayload = {
      name: trimmed,
      urgency: values.urgency,
      importance: values.importance,
      difficulty: values.difficulty,
      status: values.status,
      done: values.done,
      attemptWithAi: values.attemptWithAi,
      timeEstimateMinutes,
      parentTaskId: values.parentTaskId,
      projectIds: [...values.projectIds],
      bucketIds: [...values.bucketIds],
    };

    setSaving(true);
    setError(null);

    try {
      await onSubmit(mode === 'create' ? (payload as TaskCreatePayload) : payload);
      if (mode === 'create') {
        setValues({ ...TASK_INITIAL_VALUES });
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save task.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) {
      return;
    }

    if (!window.confirm('Delete this task? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await onDelete();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete task.';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form className="entity-form" onSubmit={handleSubmit}>
        {TASK_FIELDS.map((field) =>
          renderEntityField({
            field,
            values,
            onChange: handleChange,
            relationOptions,
            relationCreators,
          }),
        )}
        {(selectedParentTask || selectedProjects.length > 0 || selectedBuckets.length > 0) && (
          <section className="task-relations">
            <header>
              <h4>Associations</h4>
              <p className="form-hint">Jump directly to one of the linked workspaces.</p>
            </header>
            <ul className="task-relations__list">
              {selectedParentTask && (
                <li>
                  <span>Parent task:</span>
                  <span>{selectedParentTask.name}</span>
                </li>
              )}
              {selectedProjects.map((project) => (
                <li key={`project-${project.id}`}>
                  <span>Project:</span>
                  {onOpenProjectDetails ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onOpenProjectDetails(project.id)}
                    >
                      {project.name}
                    </button>
                  ) : (
                    <span>{project.name}</span>
                  )}
                </li>
              ))}
              {selectedBuckets.map((bucket) => (
                <li key={`bucket-${bucket.id}`}>
                  <span>Bucket:</span>
                  {onOpenBucketDetails ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onOpenBucketDetails(bucket.id)}
                    >
                      {bucket.name}
                    </button>
                  ) : (
                    <span>{bucket.name}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="entity-form__actions">
          {onDelete && (
            <button type="button" className="secondary-button" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <div className="entity-form__actions-right">
            <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="primary-button" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Create task' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function ProjectFormDialog({
  title,
  mode,
  initialValues,
  allBuckets,
  allTasks,
  allProjects,
  currentProject,
  onSubmit,
  onDelete,
  onClose,
  onCreateBucket,
  onOpenProjectDetails,
  onOpenTaskDetails,
}: {
  title: string;
  mode: 'create' | 'edit';
  initialValues: ProjectFormValues;
  allBuckets: Bucket[];
  allTasks: Task[];
  allProjects: Project[];
  currentProject: Project | null;
  onSubmit: (payload: ProjectCreatePayload | ProjectUpdatePayload) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
  onCreateBucket: () => void;
  onOpenProjectDetails: (projectId: number) => void;
  onOpenTaskDetails?: (taskId: number) => void;
}) {
  const [values, setValues] = useState<ProjectFormValues>(initialValues);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [taskFilter, setTaskFilter] = useState<TaskFilterOption>('all');

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  useEffect(() => {
    setTaskFilter('all');
  }, [currentProject?.id]);

  const handleChange = useCallback(<K extends keyof ProjectFormValues>(key: K, value: ProjectFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  }, []);

  const blockedParentIds = useMemo(() => {
    if (!currentProject) {
      return new Set<number>();
    }
    const map = new Map(allProjects.map((project) => [project.id, project]));
    const blocked = new Set<number>([currentProject.id]);
    const queue = [...currentProject.subprojects.map((project) => project.id)];
    while (queue.length > 0) {
      const nextId = queue.shift()!;
      if (blocked.has(nextId)) {
        continue;
      }
      blocked.add(nextId);
      const nextProject = map.get(nextId);
      if (nextProject) {
        queue.push(...nextProject.subprojects.map((child) => child.id));
      }
    }
    return blocked;
  }, [allProjects, currentProject]);

  const relationOptions = useMemo(() => {
    const parentOptions = allProjects
      .filter((project) => !blockedParentIds.has(project.id))
      .map((project) => ({ id: project.id, name: project.name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      bucketIds: allBuckets.map((bucket) => ({ id: bucket.id, name: bucket.name })),
      parentProjectId: parentOptions,
    } satisfies Partial<Record<EntityKey<ProjectFormValues>, RelationOption[]>>;
  }, [allBuckets, allProjects, blockedParentIds]);

  const tasksById = useMemo(() => new Map(allTasks.map((task) => [task.id, task])), [allTasks]);

  const filteredAssociatedTasks = useMemo(() => {
    if (!currentProject) {
      return [] as RelatedEntitySummary[];
    }
    if (taskFilter === 'all') {
      return currentProject.tasks;
    }
    return currentProject.tasks.filter((summary) => {
      const full = tasksById.get(summary.id);
      if (!full) {
        return false;
      }
      return taskMatchesFilter(full, taskFilter);
    });
  }, [currentProject, taskFilter, tasksById]);

  const relationCreators = useMemo(
    () => ({
      bucketIds: onCreateBucket,
    }),
    [onCreateBucket],
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = normalizeName(values.name);
    if (!trimmed) {
      setError('Project name cannot be empty.');
      return;
    }

    const payload: ProjectUpdatePayload = {
      name: trimmed,
      status: values.status,
      deadline: dateInputToIso(values.deadline),
      parentProjectId: values.parentProjectId,
      bucketIds: values.bucketIds,
    };

    setSaving(true);
    setError(null);

    try {
      await onSubmit(mode === 'create' ? (payload as ProjectCreatePayload) : payload);
      if (mode === 'create') {
        setValues({ ...PROJECT_INITIAL_VALUES });
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save project.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) {
      return;
    }

    if (!window.confirm('Delete this project? Associated links will be removed.')) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await onDelete();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete project.';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form className="entity-form" onSubmit={handleSubmit}>
        {PROJECT_FIELDS.map((field) =>
          renderEntityField({
            field,
            values,
            onChange: handleChange,
            relationOptions,
            relationCreators,
          }),
        )}
        {currentProject && (
          <section className="entity-summary">
            <header>
              <h4>Associated tasks</h4>
              <p className="form-hint">Manage task links from the Tasks tab.</p>
            </header>
            <TaskFilters activeFilter={taskFilter} onChange={setTaskFilter} />
            {filteredAssociatedTasks.length === 0 ? (
              <p className="form-hint">No tasks linked to this project yet.</p>
            ) : (
              <ul className="entity-summary__list">
                {filteredAssociatedTasks.map((task) => (
                  <li key={task.id}>
                    {onOpenTaskDetails ? (
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          onOpenTaskDetails(task.id);
                        }}
                      >
                        {task.name}
                      </button>
                    ) : (
                      task.name
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        {currentProject && (
          <section className="nested-projects">
            <header>
              <h4>Contained projects</h4>
              <p className="form-hint">Projects nested beneath this one inherit its bucket context.</p>
            </header>
            {currentProject.subprojects.length === 0 ? (
              <p className="form-hint">No projects nested under this project yet.</p>
            ) : (
              <ul className="nested-projects__list">
                {currentProject.subprojects.map((subproject) => (
                  <li key={subproject.id}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onOpenProjectDetails(subproject.id)}
                    >
                      {subproject.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="entity-form__actions">
          {onDelete && (
            <button type="button" className="secondary-button" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <div className="entity-form__actions-right">
            <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="primary-button" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Create project' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function BucketFormDialog({
  title,
  mode,
  initialValues,
  allProjects,
  allTasks,
  onSubmit,
  onDelete,
  onClose,
  onCreateProject,
  currentBucket,
  onOpenTaskDetails,
}: {
  title: string;
  mode: 'create' | 'edit';
  initialValues: BucketFormValues;
  allProjects: Project[];
  allTasks: Task[];
  onSubmit: (payload: BucketCreatePayload | BucketUpdatePayload) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
  onCreateProject: () => void;
  currentBucket: Bucket | null;
  onOpenTaskDetails?: (taskId: number) => void;
}) {
  const [values, setValues] = useState<BucketFormValues>(initialValues);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [taskFilter, setTaskFilter] = useState<TaskFilterOption>('all');

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  useEffect(() => {
    setTaskFilter('all');
  }, [currentBucket?.id]);

  const handleChange = useCallback(<K extends keyof BucketFormValues>(key: K, value: BucketFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  }, []);

  const relationOptions = useMemo(
    () => ({
      projectIds: allProjects.map((project) => ({ id: project.id, name: project.name })),
    }),
    [allProjects],
  );

  const tasksById = useMemo(() => new Map(allTasks.map((task) => [task.id, task])), [allTasks]);

  const filteredAssociatedTasks = useMemo(() => {
    if (!currentBucket) {
      return [] as RelatedEntitySummary[];
    }
    if (taskFilter === 'all') {
      return currentBucket.tasks;
    }
    return currentBucket.tasks.filter((summary) => {
      const full = tasksById.get(summary.id);
      if (!full) {
        return false;
      }
      return taskMatchesFilter(full, taskFilter);
    });
  }, [currentBucket, taskFilter, tasksById]);

  const relationCreators = useMemo(
    () => ({
      projectIds: onCreateProject,
    }),
    [onCreateProject],
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = normalizeName(values.name);
    if (!trimmed) {
      setError('Bucket name cannot be empty.');
      return;
    }

    const payload: BucketUpdatePayload = {
      name: trimmed,
      projectIds: values.projectIds,
    };

    setSaving(true);
    setError(null);

    try {
      await onSubmit(mode === 'create' ? (payload as BucketCreatePayload) : payload);
      if (mode === 'create') {
        setValues({ ...BUCKET_INITIAL_VALUES });
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save bucket.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) {
      return;
    }

    if (!window.confirm('Delete this bucket? Associated links will be removed.')) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await onDelete();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete bucket.';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form className="entity-form" onSubmit={handleSubmit}>
        {BUCKET_FIELDS.map((field) =>
          renderEntityField({
            field,
            values,
            onChange: handleChange,
            relationOptions,
            relationCreators,
          }),
        )}
        {currentBucket && (
          <section className="entity-summary">
            <header>
              <h4>Associated tasks</h4>
              <p className="form-hint">Manage these links from the Tasks tab.</p>
            </header>
            <TaskFilters activeFilter={taskFilter} onChange={setTaskFilter} />
            {filteredAssociatedTasks.length === 0 ? (
              <p className="form-hint">No tasks linked to this bucket yet.</p>
            ) : (
              <ul className="entity-summary__list">
                {filteredAssociatedTasks.map((task) => (
                  <li key={task.id}>
                    {onOpenTaskDetails ? (
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          onOpenTaskDetails(task.id);
                        }}
                      >
                        {task.name}
                      </button>
                    ) : (
                      task.name
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="entity-form__actions">
          {onDelete && (
            <button type="button" className="secondary-button" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <div className="entity-form__actions-right">
            <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="primary-button" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Create bucket' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function TaskEmptyState({ loading, error }: { loading: boolean; error: string | null }) {
  if (loading) {
    return <p className="task-status-message">Loading tasks…</p>;
  }

  if (error) {
    return (
      <p className="task-status-message task-status-message--error" role="alert">
        {error}
      </p>
    );
  }

  return <p className="task-status-message">No tasks match this filter. Try adding new work above.</p>;
}

function TasksView({
  tasks,
  loading,
  error,
  onRefresh,
  onOpenTask,
  onOpenCreateTask,
}: {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onOpenTask: (task: Task) => void;
  onOpenCreateTask: () => void;
}) {
  const [filter, setFilter] = useState<TaskFilterOption>('all');
  const filteredTasks = useMemo(() => tasks.filter((task) => taskMatchesFilter(task, filter)), [tasks, filter]);

  return (
    <div className="tasks-view">
      <div className="tasks-view__header">
        <div>
          <h2>Tasks</h2>
          <p>Track work across urgency, importance, and focus while mapping tasks to the right projects and buckets.</p>
        </div>
        <div className="tasks-view__actions">
          <button type="button" className="primary-button" onClick={onOpenCreateTask} disabled={loading}>
            New task
          </button>
          <button type="button" className="secondary-button" onClick={onRefresh} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      <TaskFilters activeFilter={filter} onChange={setFilter} />
      {filteredTasks.length === 0 ? (
        <TaskEmptyState loading={loading} error={error} />
      ) : (
        <ul className="task-list">
          {filteredTasks.map((task) => (
            <TaskCard key={task.id} task={task} onOpen={onOpenTask} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Pill({ label }: { label: string }) {
  return <span className="pill">{label}</span>;
}

function EntityChips({ items }: { items: RelatedEntitySummary[] }) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="entity-chips">
      {items.map((item) => (
        <Pill key={item.id} label={item.name} />
      ))}
    </div>
  );
}

function ProjectCard({
  project,
  onOpen,
  onOpenTask,
  taskFilter,
  tasksById,
}: {
  project: Project;
  onOpen: (project: Project) => void;
  onOpenTask: (taskId: number) => void;
  taskFilter: TaskFilterOption;
  tasksById: Map<number, Task>;
}) {
  const deadline = project.deadline ? formatDate(project.deadline) : 'No deadline';
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(project);
    }
  };
  const visibleTasks = useMemo(() => {
    return project.tasks.filter((summary) => {
      if (taskFilter === 'all') {
        return true;
      }
      const full = tasksById.get(summary.id);
      if (!full) {
        return false;
      }
      return taskMatchesFilter(full, taskFilter);
    });
  }, [project.tasks, taskFilter, tasksById]);
  return (
    <article
      className="project-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(project)}
      onKeyDown={handleKeyDown}
    >
      <header className="project-card__header">
        <div>
          <h3>{project.name}</h3>
          <p>{deadline}</p>
          {project.parentProject && (
            <p className="project-card__parent">Part of {project.parentProject.name}</p>
          )}
        </div>
        <Pill label={project.status.replace(/_/g, ' ')} />
      </header>
      <EntityChips items={project.buckets} />
      {visibleTasks.length > 0 ? (
        <ul className="project-card__tasks">
          {visibleTasks.slice(0, 3).map((task) => (
            <li key={task.id}>
              <button
                type="button"
                className="project-card__task-button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenTask(task.id);
                }}
              >
                {task.name}
              </button>
            </li>
          ))}
          {visibleTasks.length > 3 && <li className="project-card__tasks-more">+{visibleTasks.length - 3} more</li>}
        </ul>
      ) : taskFilter !== 'all' && project.tasks.length > 0 ? (
        <p className="project-card__empty">No tasks match this filter.</p>
      ) : null}
    </article>
  );
}

function ProjectsView({
  projects,
  allTasks,
  onOpenProject,
  onOpenCreateProject,
  onOpenTask,
}: {
  projects: Project[];
  allTasks: Task[];
  onOpenProject: (project: Project) => void;
  onOpenCreateProject: () => void;
  onOpenTask: (taskId: number) => void;
}) {
  const [filter, setFilter] = useState<TaskFilterOption>('all');
  const tasksById = useMemo(() => new Map(allTasks.map((task) => [task.id, task])), [allTasks]);
  const grouped = useMemo(() => {
    return PROJECT_STATUS_ORDER.map((status) => ({
      status,
      items: projects.filter((project) => project.status === status),
    }));
  }, [projects]);

  return (
    <div className="projects-view">
      <div className="projects-view__header">
        <div>
          <h2>Projects</h2>
          <p>Organize related tasks into focused efforts and map them to their supporting buckets.</p>
        </div>
        <button type="button" className="primary-button" onClick={onOpenCreateProject}>
          New project
        </button>
      </div>
      <TaskFilters activeFilter={filter} onChange={setFilter} />
      <div className="projects-view__board">
        {grouped.map((group) => (
          <section key={group.status} className="projects-view__column">
            <header>
              <h3>{PROJECT_STATUS_TITLES[group.status]}</h3>
              <span className="projects-view__count">{group.items.length}</span>
            </header>
            <div className="projects-view__column-body">
              {group.items.length === 0 ? (
                <p className="projects-view__empty">No projects yet.</p>
              ) : (
                group.items.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onOpen={onOpenProject}
                    onOpenTask={onOpenTask}
                    taskFilter={filter}
                    tasksById={tasksById}
                  />
                ))
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function BucketCard({
  bucket,
  onOpen,
  onOpenTask,
  taskFilter,
  tasksById,
}: {
  bucket: Bucket;
  onOpen: (bucket: Bucket) => void;
  onOpenTask: (taskId: number) => void;
  taskFilter: TaskFilterOption;
  tasksById: Map<number, Task>;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(bucket);
    }
  };
  const visibleTasks = useMemo(() => {
    return bucket.tasks.filter((summary) => {
      if (taskFilter === 'all') {
        return true;
      }
      const full = tasksById.get(summary.id);
      if (!full) {
        return false;
      }
      return taskMatchesFilter(full, taskFilter);
    });
  }, [bucket.tasks, taskFilter, tasksById]);
  return (
    <article
      className="bucket-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(bucket)}
      onKeyDown={handleKeyDown}
    >
      <header>
        <h3>{bucket.name}</h3>
        <p>Created {formatDate(bucket.createdAt)}</p>
      </header>
      <EntityChips items={bucket.projects} />
      {visibleTasks.length > 0 ? (
        <ul className="bucket-card__tasks">
          {visibleTasks.slice(0, 4).map((task) => (
            <li key={task.id}>
              <button
                type="button"
                className="bucket-card__task-button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenTask(task.id);
                }}
              >
                {task.name}
              </button>
            </li>
          ))}
          {visibleTasks.length > 4 && <li className="bucket-card__tasks-more">+{visibleTasks.length - 4} more</li>}
        </ul>
      ) : taskFilter !== 'all' && bucket.tasks.length > 0 ? (
        <p className="bucket-card__empty">No tasks match this filter.</p>
      ) : null}
    </article>
  );
}

function BucketsView({
  buckets,
  allTasks,
  onOpenBucket,
  onOpenCreateBucket,
  onOpenTask,
}: {
  buckets: Bucket[];
  allTasks: Task[];
  onOpenBucket: (bucket: Bucket) => void;
  onOpenCreateBucket: () => void;
  onOpenTask: (taskId: number) => void;
}) {
  const [filter, setFilter] = useState<TaskFilterOption>('all');
  const tasksById = useMemo(() => new Map(allTasks.map((task) => [task.id, task])), [allTasks]);
  return (
    <div className="buckets-view">
      <div className="buckets-view__header">
        <div>
          <h2>Buckets</h2>
          <p>Keep initiatives grouped and surface the projects and tasks they connect.</p>
        </div>
        <button type="button" className="primary-button" onClick={onOpenCreateBucket}>
          New bucket
        </button>
      </div>
      <TaskFilters activeFilter={filter} onChange={setFilter} />
      <div className="buckets-view__grid">
        {buckets.length === 0 ? (
          <p className="buckets-view__empty">No buckets yet. Use the New bucket button to create one.</p>
        ) : (
          buckets.map((bucket) => (
            <BucketCard
              key={bucket.id}
              bucket={bucket}
              onOpen={onOpenBucket}
              onOpenTask={onOpenTask}
              taskFilter={filter}
              tasksById={tasksById}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TaskManagementNavigation() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!['/tasks', '/projects', '/buckets'].includes(location.pathname)) {
      navigate('/tasks');
    }
  }, [location.pathname, navigate]);

  return (
    <nav className="task-management-nav" aria-label="Task management views">
      <NavLink to="/tasks" className="task-management-nav__link">
        Tasks
      </NavLink>
      <NavLink to="/projects" className="task-management-nav__link">
        Projects
      </NavLink>
      <NavLink to="/buckets" className="task-management-nav__link">
        Buckets
      </NavLink>
    </nav>
  );
}

function taskToFormValues(task: Task): TaskFormValues {
  return {
    name: task.name,
    urgency: task.urgency,
    importance: task.importance,
    difficulty: task.difficulty,
    status: task.status,
    done: task.done,
    attemptWithAi: task.attemptWithAi,
    timeEstimateMinutes: task.timeEstimateMinutes?.toString() ?? '',
    parentTaskId: task.parentTask ? task.parentTask.id : null,
    projectIds: task.projects.map((project) => project.id),
    bucketIds: task.buckets.map((bucket) => bucket.id),
  };
}

function projectToFormValues(project: Project): ProjectFormValues {
  return {
    name: project.name,
    status: project.status,
    deadline: isoDateToInput(project.deadline),
    parentProjectId: project.parentProject ? project.parentProject.id : null,
    bucketIds: project.buckets.map((bucket) => bucket.id),
  };
}

function bucketToFormValues(bucket: Bucket): BucketFormValues {
  return {
    name: bucket.name,
    projectIds: bucket.projects.map((project) => project.id),
  };
}

export function TaskManagementFeature({}: FeatureContext) {
  const {
    tasks,
    projects,
    buckets,
    setTasks,
    setProjects,
    setBuckets,
    loading,
    error,
    reload,
  } = useTaskManagementData();

  const refreshAll = useCallback(async () => {
    await reload();
  }, [reload]);

  const [taskDialog, setTaskDialog] = useState<{ mode: 'create' | 'edit'; task: Task | null } | null>(null);
  const [projectDialog, setProjectDialog] = useState<{ mode: 'create' | 'edit'; project: Project | null } | null>(null);
  const [bucketDialog, setBucketDialog] = useState<{ mode: 'create' | 'edit'; bucket: Bucket | null } | null>(null);

  const handleTaskCreate = useCallback(
    async (payload: TaskCreatePayload) => {
      const created = await api.createTask(payload);
      setTasks((current) => [created, ...current]);
      const [projectData, bucketData] = await Promise.all([api.listProjects(), api.listBuckets()]);
      setProjects(projectData);
      setBuckets(bucketData);
    },
    [setTasks, setProjects, setBuckets],
  );

  const handleTaskUpdate = useCallback(
    async (taskId: number, payload: TaskUpdatePayload) => {
      await api.updateTask(taskId, payload);
      await refreshAll();
    },
    [refreshAll],
  );

  const handleTaskDelete = useCallback(
    async (taskId: number) => {
      await api.deleteTask(taskId);
      await refreshAll();
    },
    [refreshAll],
  );

  const handleProjectCreate = useCallback(
    async (payload: ProjectCreatePayload) => {
      await api.createProject(payload);
      await refreshAll();
    },
    [refreshAll],
  );

  const handleProjectUpdate = useCallback(
    async (projectId: number, payload: ProjectUpdatePayload) => {
      await api.updateProject(projectId, payload);
      await refreshAll();
    },
    [refreshAll],
  );

  const handleProjectDelete = useCallback(
    async (projectId: number) => {
      await api.deleteProject(projectId);
      await refreshAll();
    },
    [refreshAll],
  );

  const handleBucketCreate = useCallback(
    async (payload: BucketCreatePayload) => {
      await api.createBucket(payload);
      await refreshAll();
    },
    [refreshAll],
  );

  const handleBucketUpdate = useCallback(
    async (bucketId: number, payload: BucketUpdatePayload) => {
      await api.updateBucket(bucketId, payload);
      await refreshAll();
    },
    [refreshAll],
  );

  const handleBucketDelete = useCallback(
    async (bucketId: number) => {
      await api.deleteBucket(bucketId);
      await refreshAll();
    },
    [refreshAll],
  );

  return (
    <section className="task-management">
      <MemoryRouter initialEntries={['/tasks']}>
        <TaskManagementNavigation />
        <Routes>
          <Route
            path="/tasks"
            element={(
              <TasksView
                tasks={tasks}
                loading={loading}
                error={error}
                onRefresh={refreshAll}
                onOpenTask={(task) => setTaskDialog({ mode: 'edit', task })}
                onOpenCreateTask={() => setTaskDialog({ mode: 'create', task: null })}
              />
            )}
          />
          <Route
            path="/projects"
            element={(
              <ProjectsView
                projects={projects}
                allTasks={tasks}
                onOpenProject={(project) => setProjectDialog({ mode: 'edit', project })}
                onOpenCreateProject={() => setProjectDialog({ mode: 'create', project: null })}
                onOpenTask={(taskId) => {
                  const targetTask = tasks.find((task) => task.id === taskId);
                  if (targetTask) {
                    setProjectDialog(null);
                    setTaskDialog({ mode: 'edit', task: targetTask });
                  }
                }}
              />
            )}
          />
          <Route
            path="/buckets"
            element={(
              <BucketsView
                buckets={buckets}
                allTasks={tasks}
                onOpenBucket={(bucket) => setBucketDialog({ mode: 'edit', bucket })}
                onOpenCreateBucket={() => setBucketDialog({ mode: 'create', bucket: null })}
                onOpenTask={(taskId) => {
                  const targetTask = tasks.find((task) => task.id === taskId);
                  if (targetTask) {
                    setBucketDialog(null);
                    setTaskDialog({ mode: 'edit', task: targetTask });
                  }
                }}
              />
            )}
          />
        </Routes>
      </MemoryRouter>

      {taskDialog && (
        <TaskFormDialog
          title={taskDialog.mode === 'create' ? 'Create task' : 'Edit task'}
          mode={taskDialog.mode}
          initialValues={taskDialog.task ? taskToFormValues(taskDialog.task) : { ...TASK_INITIAL_VALUES }}
          projects={projects}
          buckets={buckets}
          tasks={tasks}
          currentTask={taskDialog.task ?? null}
          onSubmit={async (payload) => {
            if (taskDialog.mode === 'create') {
              await handleTaskCreate(payload as TaskCreatePayload);
            } else if (taskDialog.task) {
              await handleTaskUpdate(taskDialog.task.id, payload as TaskUpdatePayload);
            }
          }}
          onDelete={
            taskDialog.mode === 'edit' && taskDialog.task
              ? () => handleTaskDelete(taskDialog.task!.id)
              : undefined
          }
          onClose={() => setTaskDialog(null)}
          onCreateProject={() => setProjectDialog({ mode: 'create', project: null })}
          onCreateBucket={() => setBucketDialog({ mode: 'create', bucket: null })}
          onOpenProjectDetails={(projectId) => {
            const target = projects.find((project) => project.id === projectId);
            if (target) {
              setTaskDialog(null);
              setProjectDialog({ mode: 'edit', project: target });
            }
          }}
          onOpenBucketDetails={(bucketId) => {
            const target = buckets.find((bucket) => bucket.id === bucketId);
            if (target) {
              setTaskDialog(null);
              setBucketDialog({ mode: 'edit', bucket: target });
            }
          }}
        />
      )}

      {projectDialog && (
        <ProjectFormDialog
          title={projectDialog.mode === 'create' ? 'Create project' : 'Edit project'}
          mode={projectDialog.mode}
          initialValues={
            projectDialog.project ? projectToFormValues(projectDialog.project) : { ...PROJECT_INITIAL_VALUES }
          }
          allBuckets={buckets}
          allTasks={tasks}
          allProjects={projects}
          currentProject={projectDialog.project}
          onSubmit={async (payload) => {
            if (projectDialog.mode === 'create') {
              await handleProjectCreate(payload as ProjectCreatePayload);
            } else if (projectDialog.project) {
              await handleProjectUpdate(projectDialog.project.id, payload as ProjectUpdatePayload);
            }
          }}
          onDelete={
            projectDialog.mode === 'edit' && projectDialog.project
              ? () => handleProjectDelete(projectDialog.project!.id)
              : undefined
          }
          onClose={() => setProjectDialog(null)}
          onCreateBucket={() => setBucketDialog({ mode: 'create', bucket: null })}
          onOpenProjectDetails={(projectId) => {
            const target = projects.find((project) => project.id === projectId);
            if (target) {
              setProjectDialog({ mode: 'edit', project: target });
            }
          }}
          onOpenTaskDetails={(taskId) => {
            const targetTask = tasks.find((task) => task.id === taskId);
            if (targetTask) {
              setProjectDialog(null);
              setTaskDialog({ mode: 'edit', task: targetTask });
            }
          }}
        />
      )}

      {bucketDialog && (
        <BucketFormDialog
          title={bucketDialog.mode === 'create' ? 'Create bucket' : 'Edit bucket'}
          mode={bucketDialog.mode}
          initialValues={
            bucketDialog.bucket ? bucketToFormValues(bucketDialog.bucket) : { ...BUCKET_INITIAL_VALUES }
          }
          allProjects={projects}
          allTasks={tasks}
          onSubmit={async (payload) => {
            if (bucketDialog.mode === 'create') {
              await handleBucketCreate(payload as BucketCreatePayload);
            } else if (bucketDialog.bucket) {
              await handleBucketUpdate(bucketDialog.bucket.id, payload as BucketUpdatePayload);
            }
          }}
          onDelete={
            bucketDialog.mode === 'edit' && bucketDialog.bucket
              ? () => handleBucketDelete(bucketDialog.bucket!.id)
              : undefined
          }
          onClose={() => setBucketDialog(null)}
          onCreateProject={() => setProjectDialog({ mode: 'create', project: null })}
          currentBucket={bucketDialog.bucket}
          onOpenTaskDetails={(taskId) => {
            const targetTask = tasks.find((task) => task.id === taskId);
            if (targetTask) {
              setBucketDialog(null);
              setTaskDialog({ mode: 'edit', task: targetTask });
            }
          }}
        />
      )}
    </section>
  );
}
